import React, { FC, useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { logError } from '@modernloop/shared/utils';
import { Box, Stack, StackProps, Typography } from '@mui/material';

import { SourceType } from 'src/generated/mloop-graphql';

import { useAiAgentLabel } from 'src/entities/ActivityLog/Helpers/useAiAgentLabel';
import ApplicationDetailsActivityLogsList from 'src/entities/ActivityLogsList/ApplicationDetailsActivityLogsList';
import TaskDetailsActivityLogsList from 'src/entities/ActivityLogsList/TaskDetailsActivityLogsList';
import { shouldSafetyPollAgentActivityLogs } from 'src/entities/ActivityLogsList/agentActivityLogSafetyPoll';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

import ActivityFeedAgentStatus from './ActivityFeedAgentStatus';
import ActivityFeedComposer from './ActivityFeedComposer';
import ActivityFeedTabs from './ActivityFeedTabs';
import {
  ActivityFeedTabs as ActivityFeedTab,
  getActivityTypesForActivityFeedTab,
  getActorTypesForActivityFeedTab,
} from './activityFeedFilters';

interface Props {
  sourceId: string;
  sourceType: SourceType;
  onHasUnsavedNoteChangesChange?: (value: boolean) => void;
  onNoteCreated?: () => void;
  aiEnabled?: boolean;
  delegateStatus?: string | null;
}

const HAS_NOTE_COMPOSER = {
  [SourceType.SourceTypeTask]: true,
  [SourceType.SourceTypeApplication]: false,
};

/**
 * Renders the conventional top-down activity feed for task and application detail pages.
 */
const ActivityFeed: FC<Props> = ({
  sourceId,
  sourceType,
  onHasUnsavedNoteChangesChange,
  onNoteCreated,
  aiEnabled = false,
  delegateStatus,
}) => {
  const { t } = useTranslation();
  const aiTaskEnabled = useIsAiTaskEnabled();
  const agentFeaturesEnabled = aiEnabled && aiTaskEnabled;
  const aiAgentLabel = useAiAgentLabel();
  const refetchData = useActivityFeedRefetch(sourceType);
  const safetyPollActivityLogs = shouldSafetyPollAgentActivityLogs(agentFeaturesEnabled, delegateStatus);

  const [currentTab, setCurrentTab] = useState<ActivityFeedTab>(ActivityFeedTab.All);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const hasNoteComposer = useMemo(() => {
    const ret = HAS_NOTE_COMPOSER[sourceType];

    if (ret === undefined) {
      logError(`${sourceType} needs a composer value`);
      return false;
    }

    return ret;
  }, [sourceType]);

  const stackOptions: StackProps = useMemo(() => {
    if (sourceType === SourceType.SourceTypeApplication) {
      return { direction: 'row', justifyContent: 'space-between', alignItems: 'center' };
    }

    return { direction: 'column', spacing: 1 };
  }, [sourceType]);

  const handleNoteChange = (value: string) => {
    if (value && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
      if (onHasUnsavedNoteChangesChange) {
        onHasUnsavedNoteChangesChange(true);
      }
    } else if (!value && hasUnsavedChanges) {
      setHasUnsavedChanges(false);
      if (onHasUnsavedNoteChangesChange) {
        onHasUnsavedNoteChangesChange(false);
      }
    }
  };

  const onCreateNoteSubmit = () => {
    refetchData();
    if (onNoteCreated) {
      onNoteCreated();
    }
  };

  const renderActivityLogsList = () => {
    const activityTypes = getActivityTypesForActivityFeedTab(currentTab);

    if (sourceType === SourceType.SourceTypeTask) {
      return (
        <TaskDetailsActivityLogsList
          key={`${sourceId}-${currentTab}`}
          taskId={sourceId}
          activityTypes={activityTypes}
          safetyPollActivityLogs={safetyPollActivityLogs}
        />
      );
    }

    if (sourceType === SourceType.SourceTypeApplication) {
      return (
        <ApplicationDetailsActivityLogsList
          applicationId={sourceId}
          activityTypes={activityTypes}
          actorTypes={getActorTypesForActivityFeedTab(currentTab)}
        />
      );
    }

    return null;
  };

  const agentStatusSlot = agentFeaturesEnabled ? (
    <Box
      sx={{
        minHeight: 22,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <ActivityFeedAgentStatus
        sourceId={sourceId}
        sourceType={sourceType}
        delegateStatus={delegateStatus}
        aiAgentLabel={aiAgentLabel}
      />
    </Box>
  ) : null;

  return (
    <Stack direction="column" spacing={1} sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack {...stackOptions} sx={{ flexShrink: 0 }}>
        <Typography variant="body1" fontWeight={600}>
          {t('hiring-dashboard-common:common.activity.title')}
        </Typography>

        <ActivityFeedTabs sourceId={sourceId} sourceType={sourceType} value={currentTab} onChange={setCurrentTab} />
      </Stack>

      {hasNoteComposer ? (
        <ActivityFeedComposer
          sourceId={sourceId}
          sourceType={sourceType}
          onSubmit={onCreateNoteSubmit}
          onChange={handleNoteChange}
        />
      ) : null}
      {agentStatusSlot}

      <Stack
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {renderActivityLogsList()}
      </Stack>
    </Stack>
  );
};

export default ActivityFeed;
